import { useState, useEffect } from 'react';
import AcademicApplicationService from '../services/AcademicApplicationService';
import Swal from 'sweetalert2';

/**
 * Custom hook for managing academic application state and logic
 * @param {string} userEmail - User's email address
 * @param {boolean} isAuthenticated - Whether user is authenticated
 * @returns {Object} Academic application state and handlers
 */
export const useAcademicApplication = (userEmail, isAuthenticated = true) => {
  const [trialType, setTrialType] = useState(null);
  const [userType, setUserType] = useState(null);
  const [academicStatus, setAcademicStatus] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [activeCampaignLists, setActiveCampaignLists] = useState([]);
  const [activeCampaignTags, setActiveCampaignTags] = useState([]);
  const [nextPaymentDue, setNextPaymentDue] = useState(null);

  const normalizeTags = tags => (Array.isArray(tags) ? tags.map(tag => String(tag)) : []);

  // Fetch academic status on mount
  useEffect(() => {
    const fetchAcademicStatus = async () => {
      if (isAuthenticated && userEmail) {
        setLoadingStatus(true);
        try {
          const response = await AcademicApplicationService.getAcademicStatus(userEmail);
          setAcademicStatus(response.status);
          setTrialType(response.trialType);
          setUserType(response.userType);
          setGroups(Array.isArray(response.groups) ? response.groups : []);
          setActiveCampaignLists(response.activeCampaignLists || []);
          setActiveCampaignTags(normalizeTags(response.activeCampaignTags));
          setNextPaymentDue(response.nextPaymentDue || null);
        } catch (error) {
          console.error('Error fetching academic status:', error);
        } finally {
          setLoadingStatus(false);
        }
      } else {
        setLoadingStatus(false);
      }
    };

    fetchAcademicStatus();
  }, [isAuthenticated, userEmail]);

  const canSeePrices = () => {
    if (!isAuthenticated) return true;

    const academicTags = ['6843', '6844', '6877', '6913', '6914', '6928', '6935'];
    const hasAcademicTag = activeCampaignTags.some(tag => academicTags.includes(tag));
    const hasAcademicRole = groups.includes('ROLE_ACADEMIC');
    const isAcademicUserType = userType === 'academic';

    return hasAcademicTag || hasAcademicRole || isAcademicUserType;
  };

  /**
   * Check if user is commercial based on tags
   * Commercial users have these tags:
   * - 6848: Standard
   * - 6846: Advanced
   * - 6847: Premium
   * - 6850: Trial: Commercial
   */
  const isCommercial = () => {
    const commercialTags = ['6848', '6846', '6847', '6850'];
    return activeCampaignTags.some(tag => commercialTags.includes(tag));
  };

  /**
   * Submit academic application
   */
  const submitAcademicApplication = async () => {
    // Show loading
    Swal.fire({
      title: 'Submitting your application...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await AcademicApplicationService.applyForAcademic(userEmail);

      if (response.status === 'error') {
        if (response.contactSupport) {
          Swal.fire({
            title: 'Error',
            text: response.message,
            icon: 'error',
            showCancelButton: true,
            confirmButtonText: 'Contact Support',
            cancelButtonText: 'Close',
            customClass: {
              confirmButton: 'btn-gradient-blue',
              cancelButton: 'btn-gradient-pink',
            },
          }).then(result => {
            if (result.isConfirmed) {
              window.location.href = '/Support';
            }
          });
        } else {
          Swal.fire({
            title: 'Error',
            text: response.message,
            icon: 'error',
            customClass: {
              confirmButton: 'btn-gradient-pink',
            },
          });
        }
        return false;
      } else {
        Swal.fire({
          title: 'Application Submitted!',
          text:
            response.message ||
            'Thank you for submitting your request. Someone will review it within 7 days.',
          icon: 'success',
          customClass: {
            confirmButton: 'btn-gradient-blue',
          },
        });
        // Refresh status
        const statusResponse = await AcademicApplicationService.getAcademicStatus(userEmail);
        setAcademicStatus(statusResponse.status);
        return true;
      }
    } catch (error) {
      Swal.fire({
        title: 'Something went wrong',
        html: 'An error occurred while submitting your application. Please <a href="/Support" style="color: #667eea; text-decoration: underline;">contact us</a> for assistance.',
        icon: 'error',
        showCancelButton: true,
        confirmButtonText: 'Contact Us',
        cancelButtonText: 'Close',
        customClass: {
          confirmButton: 'btn-gradient-blue',
          cancelButton: 'btn-gradient-pink',
        },
      }).then(result => {
        if (result.isConfirmed) {
          window.location.href = '/Support';
        }
      });
      return false;
    }
  };

  return {
    trialType,
    userType,
    academicStatus,
    loadingStatus,
    activeCampaignLists, // Keep for backward compatibility
    activeCampaignTags,
    nextPaymentDue, // Field 22: NEXT_PAYMENT_DUE
    canSeePrices,
    isCommercial,
    submitAcademicApplication,
  };
};
